// Import necessary modules and configurations
import { Eventbus } from '../../utils';
import { events } from '../../utils/constants';
import logger from '../../utils/logger';
import axios from 'axios';
import config from '../../config';
import ToastService from '../toast.service';
import winzApi from './winz.api';
import matchesApi from './matches.api';
import winzCentreApi from './winzCentre.api';
import childMatchApi from './childMatch.api';
import statsApi from './stats.api';
import exportApi from './export.api';
import ratesApi from './rates.api';
import emailRecordsApi from './email.api';

import { AuthenticationService } from '../authentication.service';

// Set up the base URL for axios instance
const axiosInstance = axios.create();
axiosInstance.defaults.headers.common['content-type'] = 'application/json';
axiosInstance.defaults.baseURL = config.winzApiUrl;
axiosInstance.defaults.withCredentials = true;
// Set to keep track of ongoing requests to prevent duplicate requests
const ongoingRequests = new Set();

function isUnsafeHttpMethod(method) {
    return ['post', 'put', 'patch', 'delete'].includes((method || '').toLowerCase());
}

async function recoverUnauthorizedRequest(error) {
    return AuthenticationService.recoverUnauthorizedRequest({
        error,
        retryRequest: (requestConfig) => axiosInstance(requestConfig),
        shouldRefreshCsrf: isUnsafeHttpMethod,
        setCsrfHeader: (requestConfig, csrfToken) => {
            requestConfig.headers = {
                ...(requestConfig.headers || {}),
                'X-CSRF-TOKEN': csrfToken
            };
        },
        loginReturnUrl: `${window.location.origin}/login`
    });
}

// Request interceptor to handle request configurations
axiosInstance.interceptors.request.use(async request => {
    const requestKey = `${request.method}-${request.url}`;
    if (ongoingRequests.has(requestKey)) {
         // If request is ongoing, return the rejected promise
         const error = new axios.Cancel('Duplicate request cancelled');
         return Promise.reject(error);
    }
    ongoingRequests.add(requestKey);
    let isAuthenticated = AuthenticationService.isAuthenticated();
    if (!isAuthenticated) {
        await AuthenticationService.refreshToken();
        isAuthenticated = AuthenticationService.isAuthenticated();
    }
    if(!isAuthenticated) {
        ongoingRequests.delete(requestKey);
        return Promise.reject(new axios.Cancel('User not authenticated'));
    }

    request.withCredentials = true;

    if (isUnsafeHttpMethod(request.method)) {
        const csrfToken = await AuthenticationService.getCsrfToken();
        if (csrfToken) {
            request.headers['X-CSRF-TOKEN'] = csrfToken;
        }
    }

    return request;
}, function (error) {
    if (error.config) {
        const requestKey = `${error.config.method}-${error.config.url}`;
        ongoingRequests.delete(requestKey);
    }
    return Promise.reject(error);
});

// Response interceptor to handle response and errors
axiosInstance.interceptors.response.use((response) => {
    const requestKey = `${response.config.method}-${response.config.url}`;
    ongoingRequests.delete(requestKey);

    if (response.status >= 400) {
        const error = new Error(`HTTP Error ${response.status}`);
        error.response = response;
        error.config = response.config;
        return Promise.reject(error);
    }

    return response?.data?.data || response?.data || response;
}, (error) => {
    if (error && error.config) {
        const requestKey = `${error.config.method}-${error.config.url}`;
        ongoingRequests.delete(requestKey);
    }

    if (error?.response?.status === 401) {
        return recoverUnauthorizedRequest(error).then((recoveredResponse) => {
            if (recoveredResponse) {
                return recoveredResponse;
            }

            return Promise.reject(error);
        });
    }

    if (error && error.response) {
        // Emit unauthorized event if status is 401 or 403
        const status = error.response.status;
        if (status === 401 || status === 403) {
            Eventbus.emit(events.HTTP_UNAUTHORIZED, error);
        }

        const responseData = error.response.data || {};
        const title = responseData.statusCode || responseData.title || (status === 401 || status === 403 ? 'Unauthorized' : `Error ${status}`);
        const errors = responseData.errors || responseData.message || 'An error occurred';
        const url = error.config?.url || '';
        const method = error.config?.method || '';
        const toastId = `api-error-${method}-${url.split('/').pop() || 'unknown'}`;
        const showToast = error.config?.suppressErrorToast !== true;

        if (showToast && title && errors) {
            ToastService.displayToast({
                type: 'error',
                title,
                data: errors,
                toastId,
                copyToClipboard: true,
                options: { closeOnClick: false }
            });
        }

        logger.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status,
            title,
            errors
        });

        try {
            error.title = title;
            error.errors = errors;
            error.toastShown = showToast;
            error.loggedCentrally = true;
        } catch (e) {
            // ignore if we cannot assign
        }
    } else if (error && error.code === 'ERR_NETWORK') {
        const url = error.config?.url || '';
        const method = error.config?.method || '';
        const usesCookieSession = error.config?.withCredentials === true;
        const toastId = `api-error-${method}-${url.split('/').pop() || 'unknown'}`;

        if (usesCookieSession) {
            Eventbus.emit(events.HTTP_UNAUTHORIZED, error);
        }

        const showToast = error.config?.suppressErrorToast !== true;
        if (showToast) {
            const errorMessage = usesCookieSession
                ? 'Access denied or authorization failed. You may not have permission for this action.'
                : 'Unable to reach the server. Please check your connection.';

            ToastService.displayToast({
                type: 'error',
                title: 'Request Failed',
                data: errorMessage,
                toastId,
                copyToClipboard: true,
                options: { closeOnClick: false }
            });
        }

        logger.error('Network Error (likely CORS blocking 403/401):', {
            url,
            method,
            message: error.message,
            code: error.code,
            usesCookieSession,
            note: 'Backend may be returning 403/401 without proper CORS headers'
        });

    }
    return Promise.reject(error);
});

// Function to extract data from response
function getDataResponse(response) {
    return response.data || response;
}

// Function to build request with optional override instance
function buildRequest(overrideInstance) {
    return Promise.resolve(overrideInstance || axiosInstance);
}

// Function to make request and get data response
function makeRequest(req) {
    return req.then(getDataResponse).catch((error) => {
        if (axios.isCancel(error)) {
            return Promise.resolve({ code: 'ERR_CANCELED' });
        }
        return Promise.reject(error);
    });
}

// Main API function to return API methods
function api() {
    return {
        ...winzApi({ makeRequest, buildRequest }),
        ...matchesApi({ makeRequest, buildRequest }),
        ...winzCentreApi({ makeRequest, buildRequest }),
        ...childMatchApi({ makeRequest, buildRequest }),
        ...statsApi({ makeRequest, buildRequest }),
        ...exportApi({ makeRequest, buildRequest }),
        ...ratesApi({ makeRequest, buildRequest }),
        ...emailRecordsApi({ makeRequest, buildRequest })
    };
}

export { axiosInstance };

// Export the API function as default
export default api();